import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Wallet, Package, Users, History, Loader2 } from "lucide-react";
import { formatPrice, resolveMoney } from "@/utils/formatPrice";
import { format } from "date-fns";
import { sv } from "date-fns/locale";

// Sprint D Plan A — dual-state types: legacy kronor + _minor öre.
interface CreditPackage {
  id: string;
  organization_id: string;
  name: string;
  price: number;                       // legacy kronor
  price_minor: number | null;          // öre
  credit_value: number;                // legacy kronor
  credit_value_minor: number | null;
  currency: string;
  is_active: boolean;
  sort_order: number | null;
  vat_rate: number;
  created_at: string;
}

interface PlayerCredit {
  id: string;
  user_id: string;
  organization_id: string;
  balance: number;                     // legacy kronor
  balance_minor: number | null;        // öre
  currency: string;
  updated_at: string;
}

interface CreditTransaction {
  id: string;
  user_id: string;
  organization_id: string;
  amount: number;                      // legacy kronor
  amount_minor: number | null;         // öre
  currency: string;
  type: string;
  description: string | null;
  source: string | null;
  note: string | null;
  created_at: string;
  reference_id: string | null;
  admin_user_id: string | null;
}

export default function AdminCredits() {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const qc = useQueryClient();

  // Packages — Sprint D: select includes _minor cols for dual-state read.
  const { data: packages = [] } = useQuery({
    queryKey: ["admin-credit-packages", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("credit_packages" as any)
        .select("*")
        .eq("organization_id", organization!.id)
        .order("sort_order");
      return (data ?? []) as CreditPackage[];
    },
    enabled: !!organization?.id,
  });

  const [pkgDialog, setPkgDialog] = useState(false);
  const [editPkg, setEditPkg] = useState<CreditPackage | null>(null);
  const [pkgForm, setPkgForm] = useState({ name: "", price: 40000, credit_value: 50000, is_active: true, sort_order: 0 });

  const openNewPkg = () => {
    setEditPkg(null);
    setPkgForm({ name: "", price: 40000, credit_value: 50000, is_active: true, sort_order: 0 });
    setPkgDialog(true);
  };

  const openEditPkg = (p: CreditPackage) => {
    setEditPkg(p);
    setPkgForm({ name: p.name, price: p.price, credit_value: p.credit_value, is_active: p.is_active, sort_order: p.sort_order ?? 0 });
    setPkgDialog(true);
  };

  const savePkgMutation = useMutation({
    mutationFn: async () => {
      // Sprint D dual-write: keep legacy kronor cols + populate _minor (öre).
      const payload = {
        organization_id: organization!.id,
        name: pkgForm.name,
        price: pkgForm.price,                              // legacy kronor
        price_minor: Math.round(pkgForm.price * 100),      // öre
        credit_value: pkgForm.credit_value,                // legacy kronor
        credit_value_minor: Math.round(pkgForm.credit_value * 100),
        currency: "SEK",
        is_active: pkgForm.is_active,
        sort_order: pkgForm.sort_order,
      };
      if (editPkg?.id) {
        const { error } = await supabase.from("credit_packages" as any).update(payload).eq("id", editPkg.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("credit_packages" as any).insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-credit-packages"] });
      setPkgDialog(false);
      toast.success(t("admin:toast.creditPackageSaved"));
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("credits.couldNotSave")); },
  });

  const deletePkgMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("credit_packages" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-credit-packages"] });
      toast.success(t("admin:toast.creditPackageDeleted"));
    },
  });

  // Player balances
  const { data: playerBalances = [] } = useQuery({
    queryKey: ["admin-player-credits", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("player_credits" as any)
        .select("*")
        .eq("organization_id", organization!.id)
        .order("balance", { ascending: false });
      return (data ?? []) as PlayerCredit[];
    },
    enabled: !!organization?.id,
  });

  const { data: allProfiles = [] } = useQuery({
    queryKey: ["admin-profiles-credits"],
    queryFn: async () => {
      const { data } = await supabase.from("public_profiles" as any).select("id, full_name").order("full_name");
      return data ?? [];
    },
  });

  const profileMap = Object.fromEntries(allProfiles.map((p) => [p.id, p.full_name || "Okänd"]));

  // Manual adjustment
  const [adjustDialog, setAdjustDialog] = useState(false);
  const [adjustPlayerId, setAdjustPlayerId] = useState("");
  const [adjustAmount, setAdjustAmount] = useState(10000);
  const [adjustDesc, setAdjustDesc] = useState("");
  const [adjustSource, setAdjustSource] = useState("manual");
  const [adjustNote, setAdjustNote] = useState("");

  const adjustMutation = useMutation({
    mutationFn: async () => {
      if (!adjustPlayerId || !organization) throw new Error(t("credits.selectPlayer"));

      // Sprint D dual-state: prefer balance_minor, fall back to legacy.
      const { data: existing } = await supabase
        .from("player_credits" as any)
        .select("balance, balance_minor, currency")
        .eq("user_id", adjustPlayerId)
        .eq("organization_id", organization.id)
        .maybeSingle();

      const currentMoney = resolveMoney(
        (existing as { balance_minor?: number | null } | null)?.balance_minor,
        (existing as { balance?: number } | null)?.balance,
        (existing as { currency?: string } | null)?.currency,
      );
      const adjustAmountMinor = Math.round(adjustAmount * 100);
      const newBalanceMinor = (currentMoney?.valueMinor ?? 0) + adjustAmountMinor;
      const newBalanceKronor = Math.round(newBalanceMinor / 100);

      if (existing) {
        await supabase
          .from("player_credits" as any)
          .update({
            balance: newBalanceKronor,
            balance_minor: newBalanceMinor,
            currency: "SEK",
            updated_at: new Date().toISOString(),
          })
          .eq("user_id", adjustPlayerId)
          .eq("organization_id", organization.id);
      } else {
        await supabase
          .from("player_credits" as any)
          .insert({
            user_id: adjustPlayerId,
            organization_id: organization.id,
            balance: newBalanceKronor,
            balance_minor: newBalanceMinor,
            currency: "SEK",
          });
      }

      await supabase.from("credit_transactions" as any).insert({
        user_id: adjustPlayerId,
        organization_id: organization.id,
        amount: adjustAmount,                          // legacy kronor
        amount_minor: adjustAmountMinor,               // öre
        currency: "SEK",
        type: "admin_adjustment",
        description: adjustDesc || t("credits.manualAdjustment"),
        source: adjustSource,
        note: adjustNote || null,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-player-credits"] });
      qc.invalidateQueries({ queryKey: ["admin-credit-transactions"] });
      setAdjustDialog(false);
      setAdjustPlayerId("");
      setAdjustAmount(10000);
      setAdjustDesc("");
      setAdjustSource("manual");
      setAdjustNote("");
      toast.success(t("admin:toast.creditsAdjusted"));
    },
    onError: (e: Error) => toast.error(e.message || "Fel"),
  });

  // All transactions
  const { data: allTransactions = [] } = useQuery({
    queryKey: ["admin-credit-transactions", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("credit_transactions" as any)
        .select("*")
        .eq("organization_id", organization!.id)
        .order("created_at", { ascending: false })
        .limit(100);
      return (data ?? []) as CreditTransaction[];
    },
    enabled: !!organization?.id,
  });

  const SOURCE_LABELS: Record<string, string> = {
    manual: "Manuell",
    kassan: "Kassan",
    epassi: "ePassi",
    wellnet: "Wellnet",
    actiway: "Actiway",
    stripe: "Kort",
    purchase: "Köp",
    refund: "Återbetalning",
    system: "System",
  };

  const typeLabels: Record<string, string> = {
    purchase: "Köp",
    booking_payment: "Bokning",
    refund: "Återbetalning",
    admin_adjustment: "Justering",
  };

  return (
    <div className="space-y-4">
      <h2 className="text-sm font-semibold">{t("credits.title")}</h2>

      <Tabs defaultValue="packages">
        <TabsList>
          <TabsTrigger value="packages" className="gap-1"><Package className="h-3.5 w-3.5" />{t("credits.packages")}</TabsTrigger>
          <TabsTrigger value="players" className="gap-1"><Users className="h-3.5 w-3.5" />{t("credits.players")}</TabsTrigger>
          <TabsTrigger value="history" className="gap-1"><History className="h-3.5 w-3.5" />{t("credits.history")}</TabsTrigger>
        </TabsList>

        {/* Packages */}
        <TabsContent value="packages" className="space-y-3">
          <Button size="sm" onClick={openNewPkg}>
            <Plus className="h-4 w-4 mr-1" /> {t("credits.newPackage")}
          </Button>
          {packages.length === 0 ? (
            <p className="text-sm text-muted-foreground">Inga paket skapade ännu.</p>
          ) : (
            <div className="space-y-2">
              {packages.map((p: CreditPackage) => {
                // Sprint D dual-state: prefer _minor, fall back to legacy.
                const priceMoney = resolveMoney(p.price_minor, p.price, p.currency);
                const valueMoney = resolveMoney(p.credit_value_minor, p.credit_value, p.currency);
                const bonusMinor = (valueMoney?.valueMinor ?? 0) - (priceMoney?.valueMinor ?? 0);
                return (
                  <Card key={p.id}>
                    <CardContent className="p-3 flex items-center justify-between">
                      <div className="space-y-0.5">
                        <span className="font-medium text-sm">{p.name}</span>
                        <div className="text-xs text-muted-foreground">
                          Pris: {formatPrice(priceMoney ?? { valueMinor: 0, currency: "SEK" })} → Kredit: {formatPrice(valueMoney ?? { valueMinor: 0, currency: "SEK" })}
                          {bonusMinor > 0 && <Badge variant="default" className="ml-2 text-xs">+{formatPrice({ valueMinor: bonusMinor, currency: "SEK" })} bonus</Badge>}
                        </div>
                        {!p.is_active && <Badge variant="secondary" className="text-xs">Inaktiv</Badge>}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditPkg(p)}>{t("common.edit")}</Button>
                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => deletePkgMutation.mutate(p.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Players */}
        <TabsContent value="players" className="space-y-3">
          <Button size="sm" onClick={() => setAdjustDialog(true)}>
            <Wallet className="h-4 w-4 mr-1" /> Manuell justering
          </Button>
          {playerBalances.length === 0 ? (
            <p className="text-sm text-muted-foreground">Inga spelare med krediter ännu.</p>
          ) : (
            <div className="border rounded-lg divide-y">
              {playerBalances.map((pc) => {
                // Sprint D dual-state: prefer balance_minor.
                const balanceMoney = resolveMoney(pc.balance_minor, pc.balance, pc.currency);
                return (
                  <div key={pc.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span>{profileMap[pc.user_id] || pc.user_id.slice(0, 8)}</span>
                    <span className="font-medium">{formatPrice(balanceMoney ?? { valueMinor: 0, currency: "SEK" })}</span>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* History */}
        <TabsContent value="history" className="space-y-2">
          {allTransactions.length === 0 ? (
            <p className="text-sm text-muted-foreground">Inga transaktioner ännu.</p>
          ) : (
            <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
              {allTransactions.map((tx) => {
                // Sprint D dual-state: prefer amount_minor.
                const txMoney = resolveMoney(tx.amount_minor, tx.amount, tx.currency);
                const txMinor = txMoney?.valueMinor ?? 0;
                return (
                <div key={tx.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant={txMinor > 0 ? "default" : "secondary"} className="text-xs shrink-0">
                      {txMinor > 0 ? "+" : ""}{formatPrice(txMoney ?? { valueMinor: 0, currency: "SEK" })}
                    </Badge>
                    <span className="truncate text-muted-foreground">{profileMap[tx.user_id] || tx.user_id.slice(0, 8)}</span>
                    <span className="text-xs text-muted-foreground">{typeLabels[tx.type] ?? tx.type}</span>
                    {tx.source && tx.source !== "manual" && (
                      <Badge variant="outline" className="text-[10px] shrink-0">{SOURCE_LABELS[tx.source] ?? tx.source}</Badge>
                    )}
                  </div>
                  <div className="flex flex-col items-end shrink-0 ml-2">
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(tx.created_at), "d MMM HH:mm", { locale: sv })}
                    </span>
                    {tx.note && <span className="text-[10px] text-muted-foreground truncate max-w-32">{tx.note}</span>}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Package dialog */}
      <Dialog open={pkgDialog} onOpenChange={setPkgDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editPkg ? t("credits.editPackage") : t("credits.newPackage")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Namn</Label>
              <Input value={pkgForm.name} onChange={(e) => setPkgForm((p) => ({ ...p, name: e.target.value }))} placeholder="T.ex. Startpaket" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Pris (kr)</Label>
                <NumberInput
                  value={pkgForm.price}
                  onValueChange={(v) => setPkgForm((p) => ({ ...p, price: Math.round(v) }))}
                />
              </div>
              <div className="space-y-1">
                <Label>Kreditvärde (kr)</Label>
                <NumberInput
                  value={pkgForm.credit_value}
                  onValueChange={(v) => setPkgForm((p) => ({ ...p, credit_value: Math.round(v) }))}
                />
              </div>
            </div>
            {pkgForm.credit_value > pkgForm.price && (
              <p className="text-xs text-emerald-600 dark:text-emerald-400">
                Bonus: +{formatPrice(pkgForm.credit_value - pkgForm.price)} extra kredit
              </p>
            )}
            <div className="flex items-center gap-2">
              <Switch checked={pkgForm.is_active} onCheckedChange={(v) => setPkgForm((p) => ({ ...p, is_active: v }))} />
              <Label>Aktiv</Label>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => savePkgMutation.mutate()} disabled={!pkgForm.name || savePkgMutation.isPending}>
              {savePkgMutation.isPending ? <Loader2 className="animate-spin h-4 w-4 mr-1" /> : null}
              Spara
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust dialog */}
      <Dialog open={adjustDialog} onOpenChange={setAdjustDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("credits.manualCreditAdjustment")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Spelare</Label>
              <Select value={adjustPlayerId} onValueChange={setAdjustPlayerId}>
                <SelectTrigger><SelectValue placeholder={t("credits.selectPlayer")} /></SelectTrigger>
                <SelectContent>
                  {allProfiles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.full_name || p.id.slice(0, 8)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Belopp (kr, negativt för avdrag)</Label>
              <NumberInput
                value={adjustAmount}
                onValueChange={(v) => setAdjustAmount(Math.round(v))}
              />
            </div>
            <div className="space-y-1">
              <Label>Källa</Label>
              <Select value={adjustSource} onValueChange={setAdjustSource}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manuell justering</SelectItem>
                  <SelectItem value="kassan">Kassan</SelectItem>
                  <SelectItem value="epassi">ePassi</SelectItem>
                  <SelectItem value="wellnet">Wellnet</SelectItem>
                  <SelectItem value="actiway">Actiway</SelectItem>
                  <SelectItem value="stripe">Kortbetalning (Stripe)</SelectItem>
                  <SelectItem value="refund">Återbetalning</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Beskrivning (valfritt)</Label>
              <Input value={adjustDesc} onChange={(e) => setAdjustDesc(e.target.value)} placeholder="T.ex. Kompensation" />
            </div>
            <div className="space-y-1">
              <Label>Notering (valfritt)</Label>
              <Input value={adjustNote} onChange={(e) => setAdjustNote(e.target.value)} placeholder="T.ex. Betalade via ePassi-app" className="h-8 text-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => adjustMutation.mutate()} disabled={!adjustPlayerId || adjustMutation.isPending}>
              {adjustMutation.isPending ? <Loader2 className="animate-spin h-4 w-4 mr-1" /> : <Wallet className="h-4 w-4 mr-1" />}
              Justera
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
